import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Create an authenticated API instance
const createAuthenticatedAPI = () => {
    // Use environment variable for API URL, fallback to Railway for development
    const apiUrl = process.env.REACT_APP_API_URL || 'https://apartix-test.onrender.com';
    
    console.log('Environment variable REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
    console.log('Final API URL being used:', apiUrl);
    
    const api = axios.create({
        baseURL: apiUrl
    });
    
    // Add request interceptor for authentication and tenant headers
    api.interceptors.request.use(config => {
        const token = localStorage.getItem('access_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            console.log('Adding Authorization header with token');
        }
        const tenantName = window.location.hostname.split('.')[0];
        if (tenantName !== 'localhost' && tenantName !== 'www') {
            config.headers['X-Tenant-ID'] = tenantName;
            console.log('Adding X-Tenant-ID header:', tenantName);
        }
        console.log('Request config:', config);
        return config;
    }, error => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    });
    
    // Add response interceptor for debugging
    api.interceptors.response.use(
        response => {
            console.log('Response received:', response.status, response.config.url);
            return response;
        },
        error => {
            console.error('Response error:', error.response?.status, error.response?.data, error.config?.url);
            return Promise.reject(error);
        }
    );
    
    return api;
};

// This interceptor will run before each request, which is the key to solving the auth issue
axios.interceptors.request.use(config => {
    // Use environment variable for API URL, fallback to localhost for development
    const apiUrl = 'https://apartix-test.onrender.com';
    
    // Only set baseURL if it's not already set
    if (!config.baseURL) {
        config.baseURL = apiUrl;
    }
    
    const token = localStorage.getItem('access_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    const tenantName = window.location.hostname.split('.')[0];
    if (tenantName !== 'localhost' && tenantName !== 'www') {
        config.headers['X-Tenant-ID'] = tenantName;
    }
    return config;
}, error => {
    return Promise.reject(error);
});

export const AuthProvider = ({ children }) => {
    const [auth, setAuth] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Use useMemo to create the API instance
    const api = useMemo(() => {
        console.log('Creating API instance with useMemo');
        return createAuthenticatedAPI();
    }, []);

    // Debug: Check if api is properly created
    console.log('AuthProvider: api object:', api);
    console.log('AuthProvider: api.get function:', api?.get);
    console.log('AuthProvider: api.post function:', api?.post);

    useEffect(() => {
        const token = localStorage.getItem('access_token');
        console.log('AuthProvider: Checking for token:', token ? 'Token exists' : 'No token');
        if (token) {
            try {
                const decoded = jwtDecode(token);
                console.log('AuthProvider: Decoded token:', decoded);
                setAuth({ token, user: decoded });
            } catch (e) {
                console.error('AuthProvider: Invalid token:', e);
                // Handle invalid token
                localStorage.removeItem('access_token');
            }
        }
        setLoading(false);
    }, []);

    const login = (data) => {
        console.log('AuthProvider: Login called with data:', data);
        localStorage.setItem('access_token', data.access);
        const decoded = jwtDecode(data.access);
        console.log('AuthProvider: Login - decoded token:', decoded);
        const newAuth = { token: data.access, user: decoded };
        console.log('AuthProvider: Setting auth state to:', newAuth);
        setAuth(newAuth);
    };

    const logout = () => {
        console.log('AuthProvider: Logout called');
        localStorage.removeItem('access_token');
        setAuth(null);
    };

    const value = { auth, loading, login, logout, api };
    
    // Debug: Check the value object
    console.log('AuthProvider: value object:', value);
    console.log('AuthProvider: value.api:', value.api);
    console.log('AuthProvider: value.auth:', value.auth);

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
